/**
 *
 * Dialog Fenster für Selektion der Datenreihen bei den Aggregaten
 *
 */

import React, { useState, useEffect, useRef } from "react";
import { IonModal, IonButton, IonHeader, IonTitle, IonToolbar, IonCheckbox, IonContent, IonButtons, IonIcon, IonInfiniteScroll, IonItem, IonLabel, IonSearchbar, IonGrid, IonRow, IonCol, IonFooter } from "@ionic/react";
import { closeOutline } from 'ionicons/icons';

import "../Statistics.css";
import http from "../../../utils/http-common";
import { Aggregate } from "../StatisticsInterfaces";

type AggregateSelectDialogProps = {
    id: number,
    name: string,
    isOpen: boolean,
    callback: () => void,
    aggregates: Aggregate[],
    setAggregates: (checked: object) => void,
    aggregatableIndex: number,
    currentAggregateIndex: number | undefined,
    setReadableAggregateNames: (readableAggregateNames: string[][]) => void,
    statisticId: string | number,
}

const AggregateSelectDialog: React.FC<AggregateSelectDialogProps> = ({ id, name, isOpen, callback, aggregates, setAggregates, aggregatableIndex, currentAggregateIndex, setReadableAggregateNames, statisticId }: AggregateSelectDialogProps) => {
    const [items, setItems] = useState([]);
    const [disableInfiniteScroll, setDisableInfiniteScroll] = useState<boolean>(false);
    const amount = 25;
    const [page, setPage] = useState<number>(1);
    const [searchStr, setSearchStr] = useState<string>("");
    const [finalSearch, setFinalSearch] = useState<string>("")
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);

    // Debounce für die Suche bei Tasteneingabe
    useEffect(() => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        debounceTimer.current = setTimeout(() => {
            setFinalSearch(searchStr);
        }, 300); // 300ms Verzögerung

        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [searchStr]);

    useEffect(() => {
        if (isOpen) {
            if (id && name && statisticId) {
                setPage(1);
                fetchData(true);
            }
        }

    }, [finalSearch, id, name, isOpen, statisticId]);

    const fetchData = async (reset?: boolean) => {
        if (id && name && isOpen && statisticId) {
            const data = reset ? [] : items;
            const currentPage = reset ? 1 : page;
            const url: string = finalSearch !== "" ? `/fetchStatisticEvenMoreFilter?statistic_id=${statisticId}&name=${name}&page=${currentPage}&search=${finalSearch}` :
                `/fetchStatisticEvenMoreFilter?statistic_id=${statisticId}&name=${name}&page=${currentPage}`

            await http
                .get(url)
                .then(function (response) {
                    if (response.data.data.length > 0) {
                        //console.log(response)
                        setItems([...data, ...response.data.data]);
                        setDisableInfiniteScroll(response.data.data.length < amount || response.data.total === amount)
                        if (reset) {
                            setPage(2);
                        } else {
                            setPage((p) => p + 1)
                        }
                    } else {
                        setItems([])
                        setDisableInfiniteScroll(true);
                    }
                })
                .catch(function (error) {
                    console.log(error);
                })
        }

    }

    async function searchNext($event: CustomEvent<void>) {
        await fetchData();
        ($event.target as HTMLIonInfiniteScrollElement).complete();
    }

    const handleCheckbox = (code, content, section) => {
        const codeValue = section ? `${section}:${code}` : code;
        if (aggregates[currentAggregateIndex]["filter_" + aggregatableIndex] && aggregates[currentAggregateIndex]["filter_" + aggregatableIndex].includes(codeValue)) {
            //remove
            setAggregates((aggregates) => {
                aggregates[currentAggregateIndex]["filter_" + aggregatableIndex] = aggregates[currentAggregateIndex]["filter_" + aggregatableIndex].filter(item => { return item !== codeValue })
                return ([...aggregates])
            })
            //@ts-ignore
            setReadableAggregateNames((readableAggregateNames: string[][]) => {
                readableAggregateNames[currentAggregateIndex] = readableAggregateNames[currentAggregateIndex].filter(item => { return item !== content })
                return ([...readableAggregateNames])
            })
        } else {
            //add
            setAggregates((aggregates) => {
                if (aggregates[currentAggregateIndex]["filter_" + aggregatableIndex]) {
                    aggregates[currentAggregateIndex]["filter_" + aggregatableIndex].push(codeValue)
                } else {
                    aggregates[currentAggregateIndex]["filter_" + aggregatableIndex] = [codeValue]
                }
                return ([...aggregates])
            })
            //@ts-ignore
            setReadableAggregateNames((readableAggregateNames: string[][]) => {
                readableAggregateNames[currentAggregateIndex].push(content)
                return ([...readableAggregateNames])
            })
        }
    }

    const handleClose = () => {
        setPage(1);
        callback();
    }

    return (
        <IonModal isOpen={isOpen} className="customModal" >
            <IonHeader >
                <IonToolbar color="primary">
                    <IonTitle>Alle Datenreihen</IonTitle>
                    <IonButtons slot="end">
                        <IonButton onClick={handleClose}>
                            <IonIcon icon={closeOutline} slot="icon-only"></IonIcon>
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
                <IonToolbar>
                    <IonSearchbar value={searchStr} onIonInput={e => setSearchStr(e.detail.value!)} placeholder="Suche"></IonSearchbar>
                </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding">
                <IonGrid className="noPaddingGrid">
                    {items.length > 0 ?
                        <IonRow>
                            {items.map((item, index) => (
                                <IonCol size="12" key={index} className="checkboxCol">
                                    <IonItem lines="none" className="checkboxItemModal ion-no-padding">
                                        <IonLabel className="ion-text-wrap" style={{ paddingTop: 2 }}>{item.content}</IonLabel>
                                        <IonCheckbox
                                            color="secondary"
                                            slot="start"
                                            value={item.code}
                                            checked={aggregates[currentAggregateIndex]["filter_" + aggregatableIndex] && aggregates[currentAggregateIndex]["filter_" + aggregatableIndex].includes(item.section ? `${item.section}:${item.code}` : item.code)}
                                            className="checkbox"
                                            onClick={(e) => handleCheckbox(item.code, item.content, item.section)}
                                        />
                                        <IonLabel className="ion-text-wrap" style={{ paddingTop: 2 }} slot="end">{item.section ? `${item.section}:${item.code}` : item.code}</IonLabel>
                                    </IonItem>
                                </IonCol>

                            ))}
                        </IonRow> : "Keine Datenreihen gefunden."}
                </IonGrid>
                <div>
                    <IonInfiniteScroll threshold="100px" disabled={disableInfiniteScroll} onIonInfinite={(e: CustomEvent<void>) => searchNext(e)}>
                        <div style={{ textAlign: "center", padding: "15px 0", opacity: 0.5 }}>Weitere Datenreihen werden geladen...</div>
                    </IonInfiniteScroll>
                </div>
            </IonContent>

            <IonFooter>
                <IonToolbar>
                    <IonButtons slot="primary">
                        <IonButton onClick={handleClose}>Schließen</IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonFooter>
        </IonModal>
    )
}

export default AggregateSelectDialog;
